import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import api from '../utils/api';
import './PerformanceConfig.css';

const PerformanceConfig = () => {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.user_type !== 'coach';

  // Data state
  const [skillCategories, setSkillCategories] = useState([]);
  const [performanceAreas, setPerformanceAreas] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [goals, setGoals] = useState([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedAreas, setExpandedAreas] = useState({}); // key: "catId-areaId"

  // Inline edit state
  const [editingGoal, setEditingGoal] = useState(null); // { areaId, catId, value }
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editingArea, setEditingArea] = useState(null);

  // Add new state
  const [addingArea, setAddingArea] = useState(false);
  const [newArea, setNewArea] = useState({ name: '', description: '' });
  const [addingQuestionFor, setAddingQuestionFor] = useState(null); // "catId-areaId"
  const [newQuestion, setNewQuestion] = useState({ question_text: '', display_order: 1 });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [areasRes, questionsRes, goalsRes, levelsRes] = await Promise.all([
        api.get('/api/performance_areas'),
        api.get('/api/performance-questions'),
        api.get('/api/category-goals'),
        api.get('/api/skill-levels')
      ]);

      const areasData = areasRes.data.performance_areas || [];
      const catsData = levelsRes.data.skill_levels || [];
      setPerformanceAreas(areasData);
      setQuestions(questionsRes.data.questions || []);
      setGoals(goalsRes.data.goals || []);
      setSkillCategories(catsData);

      // Expand all categories by default
      const expCats = {};
      catsData.forEach(c => { expCats[c.id] = true; });
      setExpandedCategories(expCats);
    } catch (err) {
      setError('Failed to load configuration data');
    } finally {
      setLoading(false);
    }
  };

  const clearMessages = () => { setError(''); setSuccess(''); };

  const showSuccess = (msg) => {
    setSuccess(msg);
    setError('');
    setTimeout(() => setSuccess(''), 3000);
  };

  // ─── Performance Area CRUD ───
  const handleAddArea = async () => {
    if (!newArea.name.trim()) return;
    clearMessages();
    try {
      await api.post('/api/performance_areas', { name: newArea.name, description: newArea.description, is_active: true });
      showSuccess('Performance area created');
      setAddingArea(false);
      setNewArea({ name: '', description: '' });
      fetchAllData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create area');
    }
  };

  const handleUpdateArea = async (area) => {
    clearMessages();
    try {
      await api.put(`/api/performance_areas/${area.id}`, {
        name: editingArea.name,
        description: editingArea.description,
        is_active: area.is_active
      });
      showSuccess('Area updated');
      setEditingArea(null);
      fetchAllData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update area');
    }
  };

  const handleDeleteArea = async (areaId) => {
    if (!window.confirm('Delete this performance area?')) return;
    clearMessages();
    try {
      await api.delete(`/api/performance_areas/${areaId}`);
      showSuccess('Area deleted');
      fetchAllData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete area');
    }
  };

  // ─── Question CRUD ───
  const handleAddQuestion = async (areaId) => {
    if (!newQuestion.question_text.trim()) return;
    clearMessages();
    try {
      await api.post('/api/performance-questions', {
        performance_area_id: areaId,
        question_text: newQuestion.question_text,
        question_type: 'slider',
        max_score: 10,
        display_order: newQuestion.display_order
      });
      showSuccess('Question added');
      setAddingQuestionFor(null);
      setNewQuestion({ question_text: '', display_order: 1 });
      fetchAllData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add question');
    }
  };

  const handleUpdateQuestion = async (q) => {
    clearMessages();
    try {
      await api.put(`/api/performance-questions/${q.id}`, {
        performance_area_id: q.performance_area_id,
        question_text: editingQuestion.question_text,
        question_type: q.question_type || 'slider',
        max_score: q.max_score || 10,
        display_order: editingQuestion.display_order,
        is_active: true
      });
      showSuccess('Question updated');
      setEditingQuestion(null);
      fetchAllData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update question');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Delete this question?')) return;
    clearMessages();
    try {
      await api.delete(`/api/performance-questions/${questionId}`);
      showSuccess('Question deleted');
      fetchAllData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete question');
    }
  };

  // ─── Goal / Target CRUD ───
  const getGoal = (areaId, catId) => {
    return goals.find(g =>
      g.performance_area_id === areaId && (g.skill_category_id === catId || g.skill_level_id === catId)
    );
  };

  const handleSaveGoal = async (areaId, catId) => {
    clearMessages();
    const existing = getGoal(areaId, catId);
    const value = parseFloat(editingGoal.value);
    if (isNaN(value) || value < 0 || value > 10) {
      setError('Target must be between 0 and 10');
      return;
    }
    try {
      if (existing) {
        await api.put(`/api/category-goals/${existing.id}`, { goal_score: value });
      } else {
        await api.post('/api/category-goals', {
          skill_level_id: catId,
          performance_area_id: areaId,
          goal_score: value
        });
      }
      showSuccess('Target saved');
      setEditingGoal(null);
      fetchAllData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save target');
    }
  };

  const handleDeleteGoal = async (goalId) => {
    clearMessages();
    try {
      await api.delete(`/api/category-goals/${goalId}`);
      showSuccess('Target removed');
      fetchAllData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove target');
    }
  };

  // ─── Toggles ───
  const toggleCategory = (catId) => {
    setExpandedCategories(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  const toggleArea = (catId, areaId) => {
    const key = `${catId}-${areaId}`;
    setExpandedAreas(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div className="pc-loading">
        <div className="pc-loading-ring"><div /><div /><div /><div /></div>
        <p>Loading configuration...</p>
      </div>
    );
  }

  return (
    <div className="pc-root">
      {/* Toasts */}
      {error && <div className="pc-toast pc-toast-err"><span className="pc-toast-icon">⚠️</span>{error}<button className="pc-toast-close" onClick={() => setError('')}>✕</button></div>}
      {success && <div className="pc-toast pc-toast-ok"><span className="pc-toast-icon">✅</span>{success}</div>}

      {/* Hero Banner */}
      <div className="pc-hero">
        <div className="pc-hero-glow" />
        <div className="pc-hero-content">
          <div className="pc-hero-left">
            <div className="pc-hero-badge">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 15l-2 5l9-11h-5l2-5L7 15h5z"/></svg>
              Performance Engine
            </div>
            <h2 className="pc-hero-title">Configuration</h2>
          </div>
          <div className="pc-hero-mid">
            <div className="pc-hstat">
              <span className="pc-hstat-num">{skillCategories.length}</span>
              <span className="pc-hstat-lbl">Levels</span>
            </div>
            <div className="pc-hstat-div" />
            <div className="pc-hstat">
              <span className="pc-hstat-num">{performanceAreas.length}</span>
              <span className="pc-hstat-lbl">Areas</span>
            </div>
            <div className="pc-hstat-div" />
            <div className="pc-hstat">
              <span className="pc-hstat-num">{questions.length}</span>
              <span className="pc-hstat-lbl">Questions</span>
            </div>
          </div>
          {isAdmin && (
            <button className="pc-hero-btn" onClick={() => setAddingArea(true)}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add Area
            </button>
          )}
        </div>
      </div>

      {/* Add Area Form */}
      {addingArea && (
        <div className="pc-add-form">
          <div className="pc-add-form-dot" />
          <input
            type="text"
            placeholder="Area name (e.g. Batting)"
            value={newArea.name}
            onChange={e => setNewArea({ ...newArea, name: e.target.value })}
            className="pc-input"
            autoFocus
          />
          <input
            type="text"
            placeholder="Description (optional)"
            value={newArea.description}
            onChange={e => setNewArea({ ...newArea, description: e.target.value })}
            className="pc-input pc-input-grow"
          />
          <div className="pc-add-form-btns">
            <button className="pc-btn pc-btn-save" onClick={handleAddArea}>Save</button>
            <button className="pc-btn pc-btn-ghost" onClick={() => { setAddingArea(false); setNewArea({ name: '', description: '' }); }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Hierarchy */}
      {skillCategories.length === 0 ? (
        <div className="pc-empty">
          <span className="pc-empty-icon">📊</span>
          <p>No skill categories found.</p>
        </div>
      ) : (
        <div className="pc-stack">
          {skillCategories.map(cat => {
            const isCatExpanded = expandedCategories[cat.id];
            return (
              <div key={cat.id} className={`pc-cat ${isCatExpanded ? 'pc-cat-open' : ''}`}>
                {/* Category Header */}
                <div className="pc-cat-hdr" onClick={() => toggleCategory(cat.id)}>
                  <div className="pc-cat-left">
                    <span className={`pc-chevron ${isCatExpanded ? 'pc-chevron-open' : ''}`}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                    </span>
                    <span className="pc-cat-name">{cat.name}</span>
                    {cat.description && <span className="pc-cat-desc">{cat.description}</span>}
                  </div>
                  <div className="pc-cat-right">
                    <span className="pc-pill pc-pill-green">{performanceAreas.length} areas</span>
                  </div>
                </div>

                {/* Category Body */}
                {isCatExpanded && (
                  <div className="pc-cat-body">
                    {performanceAreas.length === 0 ? (
                      <p className="pc-no-data">No performance areas configured yet.</p>
                    ) : (
                      performanceAreas
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(area => {
                          const areaKey = `${cat.id}-${area.id}`;
                          const goal = getGoal(area.id, cat.id);
                          const isAreaExpanded = expandedAreas[areaKey];
                          const areaQuestions = questions
                            .filter(q => q.performance_area_id === area.id || q.area_id === area.id)
                            .sort((a, b) => a.display_order - b.display_order);
                          const isEditingThisGoal = editingGoal && editingGoal.areaId === area.id && editingGoal.catId === cat.id;

                          return (
                            <div key={area.id} className={`pc-area ${isAreaExpanded ? 'pc-area-open' : ''}`}>
                              {/* Area Row */}
                              <div className="pc-area-hdr">
                                <div className="pc-area-left" onClick={() => toggleArea(cat.id, area.id)}>
                                  <span className={`pc-chevron pc-chevron-sm ${isAreaExpanded ? 'pc-chevron-open' : ''}`}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                                  </span>
                                  {editingArea && editingArea.id === area.id ? (
                                    <div className="pc-inline-edit" onClick={e => e.stopPropagation()}>
                                      <input type="text" value={editingArea.name}
                                        onChange={e => setEditingArea({ ...editingArea, name: e.target.value })}
                                        className="pc-input pc-input-sm" />
                                      <input type="text" value={editingArea.description} placeholder="Description"
                                        onChange={e => setEditingArea({ ...editingArea, description: e.target.value })}
                                        className="pc-input pc-input-sm" />
                                      <button className="pc-ibtn pc-ibtn-ok" onClick={() => handleUpdateArea(area)}>✓</button>
                                      <button className="pc-ibtn pc-ibtn-no" onClick={() => setEditingArea(null)}>✕</button>
                                    </div>
                                  ) : (
                                    <span className="pc-area-name">{area.name}</span>
                                  )}
                                </div>

                                <div className="pc-area-right" onClick={e => e.stopPropagation()}>
                                  {/* Target */}
                                  <div className="pc-target-chip">
                                    <span className="pc-target-lbl">Target</span>
                                    {isEditingThisGoal ? (
                                      <div className="pc-target-edit">
                                        <input type="number" min="0" max="10" step="0.1"
                                          value={editingGoal.value}
                                          onChange={e => setEditingGoal({ ...editingGoal, value: e.target.value })}
                                          className="pc-input pc-input-xs" autoFocus
                                          onKeyDown={e => {
                                            if (e.key === 'Enter') handleSaveGoal(area.id, cat.id);
                                            if (e.key === 'Escape') setEditingGoal(null);
                                          }} />
                                        <button className="pc-ibtn pc-ibtn-ok" onClick={() => handleSaveGoal(area.id, cat.id)}>✓</button>
                                        <button className="pc-ibtn pc-ibtn-no" onClick={() => setEditingGoal(null)}>✕</button>
                                      </div>
                                    ) : (
                                      <div className="pc-target-val-wrap">
                                        <span
                                          className={`pc-target-val ${goal ? '' : 'pc-target-empty'}`}
                                          onClick={() => isAdmin && setEditingGoal({ areaId: area.id, catId: cat.id, value: goal ? goal.target_score : '' })}
                                          style={isAdmin ? { cursor: 'pointer' } : {}}
                                          title={isAdmin ? 'Click to edit target' : ''}
                                        >
                                          {goal ? Number(goal.target_score).toFixed(1) : '—'}
                                        </span>
                                        {isAdmin && goal && (
                                          <button className="pc-ibtn-tiny" onClick={() => handleDeleteGoal(goal.id)} title="Remove target">✕</button>
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  <span className="pc-pill pc-pill-indigo">{areaQuestions.length} Q</span>

                                  {isAdmin && !(editingArea && editingArea.id === area.id) && (
                                    <div className="pc-area-acts">
                                      <button className="pc-ibtn pc-ibtn-edit" onClick={() => setEditingArea({ id: area.id, name: area.name, description: area.description || '' })} title="Edit area">✏️</button>
                                      <button className="pc-ibtn pc-ibtn-del" onClick={() => handleDeleteArea(area.id)} title="Delete area">🗑️</button>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Questions Panel */}
                              {isAreaExpanded && (
                                <div className="pc-q-panel">
                                  <div className="pc-q-top">
                                    <h5 className="pc-q-title">
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                                      Evaluation Questions
                                    </h5>
                                    {isAdmin && (
                                      <button className="pc-btn pc-btn-sm pc-btn-add-q" onClick={() => {
                                        setAddingQuestionFor(areaKey);
                                        setNewQuestion({ question_text: '', display_order: areaQuestions.length + 1 });
                                      }}>+ Add</button>
                                    )}
                                  </div>

                                  {addingQuestionFor === areaKey && (
                                    <div className="pc-add-form pc-add-form-q">
                                      <input type="text" placeholder="Question text"
                                        value={newQuestion.question_text}
                                        onChange={e => setNewQuestion({ ...newQuestion, question_text: e.target.value })}
                                        className="pc-input pc-input-grow" autoFocus
                                        onKeyDown={e => {
                                          if (e.key === 'Enter') handleAddQuestion(area.id);
                                          if (e.key === 'Escape') setAddingQuestionFor(null);
                                        }} />
                                      <input type="number" placeholder="#" value={newQuestion.display_order}
                                        onChange={e => setNewQuestion({ ...newQuestion, display_order: parseInt(e.target.value) || 1 })}
                                        className="pc-input pc-input-xs" min="1" title="Display order" />
                                      <button className="pc-btn pc-btn-save" onClick={() => handleAddQuestion(area.id)}>Save</button>
                                      <button className="pc-btn pc-btn-ghost" onClick={() => setAddingQuestionFor(null)}>Cancel</button>
                                    </div>
                                  )}

                                  {areaQuestions.length === 0 && addingQuestionFor !== areaKey ? (
                                    <p className="pc-no-data">No questions yet.{isAdmin && ' Click "+ Add" to create one.'}</p>
                                  ) : (
                                    <div className="pc-q-list">
                                      {areaQuestions.map((q, qi) => (
                                        <div key={q.id} className="pc-q-row" style={{ animationDelay: `${qi * 0.04}s` }}>
                                          {editingQuestion && editingQuestion.id === q.id ? (
                                            <div className="pc-inline-edit pc-q-edit-row">
                                              <input type="number" value={editingQuestion.display_order}
                                                onChange={e => setEditingQuestion({ ...editingQuestion, display_order: parseInt(e.target.value) || 1 })}
                                                className="pc-input pc-input-xs" min="1" />
                                              <input type="text" value={editingQuestion.question_text}
                                                onChange={e => setEditingQuestion({ ...editingQuestion, question_text: e.target.value })}
                                                className="pc-input pc-input-grow"
                                                onKeyDown={e => {
                                                  if (e.key === 'Enter') handleUpdateQuestion(q);
                                                  if (e.key === 'Escape') setEditingQuestion(null);
                                                }} />
                                              <button className="pc-ibtn pc-ibtn-ok" onClick={() => handleUpdateQuestion(q)}>✓</button>
                                              <button className="pc-ibtn pc-ibtn-no" onClick={() => setEditingQuestion(null)}>✕</button>
                                            </div>
                                          ) : (
                                            <>
                                              <span className="pc-q-num">{q.display_order}</span>
                                              <span className="pc-q-text">{q.question_text}</span>
                                              {isAdmin && (
                                                <div className="pc-q-acts">
                                                  <button className="pc-ibtn pc-ibtn-edit" onClick={() => setEditingQuestion({ id: q.id, question_text: q.question_text, display_order: q.display_order })} title="Edit">✏️</button>
                                                  <button className="pc-ibtn pc-ibtn-del" onClick={() => handleDeleteQuestion(q.id)} title="Delete">🗑️</button>
                                                </div>
                                              )}
                                            </>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PerformanceConfig;
